import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import db from '../lib/firebaseDb';

// Define the Destination interface
export interface Destination {
  id: string;
  name: string;
  slug: string;
  description: string;
  content?: string;
  imageUrl: string;
  region: string;
  isFeatured?: boolean;
  activities?: string[];
  mapLocation?: {
    latitude: number;
    longitude: number;
  };
}

// Add this function to ensure all destinations have valid image URLs
const ensureValidImageUrls = (destinations: Destination[]): Destination[] => {
  return destinations.map(destination => {
    // Check if imageUrl exists and is valid
    if (!destination.imageUrl || destination.imageUrl.trim() === '') {
      // Set a default image based on region
      const regionDefaultImages: Record<string, string> = {
        'Aegean': 'https://images.unsplash.com/photo-1524231757912-21f4fe3a7200?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
        'Mediterranean': 'https://images.unsplash.com/photo-1719413570379-8833e69c48db?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80',
        'Marmara': 'https://images.unsplash.com/photo-1541432901042-2d8bd64b4a9b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
        'Central Anatolia': 'https://images.unsplash.com/photo-p2RbLnqWPVY?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80',
        'Black Sea': 'https://images.unsplash.com/photo-1527004013197-933c4bb611b3?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
        'Eastern Anatolia': 'https://images.unsplash.com/photo-1507608869274-d3177c8bb4c7?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
        'Southeastern Anatolia': 'https://images.unsplash.com/photo-1560260240-c6ef90a163a4?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
      };
      
      return {
        ...destination,
        imageUrl: regionDefaultImages[destination.region] || 'https://images.unsplash.com/photo-1524231757912-21f4fe3a7200?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
      };
    }
    return destination;
  });
};

// Fetch all destinations
export const fetchDestinations = async (): Promise<Destination[]> => {
  try {
    const querySnapshot = await getDocs(collection(db, 'destinations'));
    const destinations = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Destination[];
    
    // Ensure all destinations have valid image URLs
    return ensureValidImageUrls(destinations);
  } catch (error) {
    console.error('Error fetching destinations:', error);
    throw error;
  }
};

// Fetch featured destinations
export const fetchFeaturedDestinations = async (): Promise<Destination[]> => {
  try {
    const destinations = await fetchDestinations();
    return destinations.filter(destination => destination.isFeatured);
  } catch (error) {
    console.error('Error fetching featured destinations:', error);
    throw error;
  }
};

// Fetch destination by slug
export const fetchDestinationBySlug = async (slug: string): Promise<Destination | null> => {
  try {
    const destinations = await fetchDestinations();
    const destination = destinations.find(d => d.slug === slug);
    return destination || null;
  } catch (error) {
    console.error('Error fetching destination by slug:', error);
    throw error;
  }
};

// Fetch destination by ID
export const fetchDestinationById = async (id: string): Promise<Destination | null> => {
  try {
    const docRef = doc(db, 'destinations', id);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return {
        id: docSnap.id,
        ...docSnap.data()
      } as Destination;
    } else {
      return null;
    }
  } catch (error) {
    console.error('Error fetching destination by ID:', error);
    throw error;
  }
}; 